import React from 'react';

export default function Sidebar({
  categories, selectedCategory, onSelectCategory,
  onAddCategory, onEditCategory, onDeleteCategory,
  isAdmin, loading, search, onSearch,
  mobileOpen, onMobileClose
}) {
  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar panel */}
      <aside
        className={[
          'fixed lg:static inset-y-0 left-0 z-40',
          'w-64 bg-white border-r border-surface-200',
          'flex flex-col',
          'transform transition-transform duration-300 ease-in-out',
          'lg:transform-none lg:transition-none',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        ].join(' ')}
        style={{ top: '56px', height: 'calc(100vh - 56px)' }}
      >
        {/* Search */}
        <div className="p-3 border-b border-surface-100">
          <div className="relative">
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              className="w-full pl-8 pr-3 py-2 text-xs bg-surface-50 border border-surface-200 rounded-lg placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-transparent"
              placeholder="Search categories…"
              value={search}
              onChange={e => onSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Add Category — admin only */}
        {isAdmin && (
          <div className="px-3 pt-3 pb-1">
            <button onClick={onAddCategory} className="btn-primary w-full justify-center text-xs py-2">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Category
            </button>
          </div>
        )}

        {/* Category list */}
        <div className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5">
          {loading ? (
            <div className="space-y-2 pt-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-9 bg-surface-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : categories.length === 0 ? (
            <p className="text-xs text-surface-400 text-center py-6">No categories found</p>
          ) : (
            categories.map(cat => (
              <CategoryItem
                key={cat.id}
                category={cat}
                isSelected={selectedCategory?.id === cat.id}
                onSelect={() => onSelectCategory(cat)}
                onEdit={() => onEditCategory(cat)}
                onDelete={() => onDeleteCategory(cat)}
                isAdmin={isAdmin}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-surface-100">
          <p className="text-xs text-surface-400 text-center">
            {categories.length} {categories.length === 1 ? 'category' : 'categories'}
          </p>
        </div>
      </aside>
    </>
  );
}

function CategoryItem({ category, isSelected, onSelect, onEdit, onDelete, isAdmin }) {
  return (
    <div
      className={[
        'group flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-150',
        isSelected ? 'bg-primary-50 text-primary-700' : 'text-surface-600 hover:bg-surface-50 hover:text-surface-900',
      ].join(' ')}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <div className={`w-2 h-2 rounded-full shrink-0 ${isSelected ? 'bg-primary-500' : 'bg-surface-300 group-hover:bg-surface-400'}`} />
        <span className={`text-sm truncate ${isSelected ? 'font-semibold' : 'font-medium'}`}>
          {category.name}
        </span>
      </div>

      {isAdmin && (
        <div
          className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-1"
          onClick={e => e.stopPropagation()}
        >
          <button
            onClick={onEdit}
            className="p-1 rounded hover:bg-primary-100 hover:text-primary-700 transition-colors"
            title="Edit"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={onDelete}
            className="p-1 rounded hover:bg-red-100 hover:text-red-600 transition-colors"
            title="Delete"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
